import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal, WritableSignal } from '@angular/core';
import { form } from '@angular/forms/signals';
import {
  PropertyAvailabilityDto,
  PropertyCadastralDto,
  PropertyCoOwnershipDto,
  PropertyDefinitionDto,
  PropertyKeyInfoDto,
  PropertyType,
} from '@workspace/data-types';
import { AbstractSignalFormComponent } from '@workspace/ng-aura';
import { PropertyDataCleaner } from '../../../../utils/property-data.cleaner';
import { PropertyAvailabilityComponent } from '../../availability/availability.component';
import { PropertyCoOwnershipComponent } from '../../co-ownership/co-ownership.component';
import { PropertySubtypesComponent, PropertySubtypesDto } from '../../subtypes/subtypes.component';
import { PropertySurfaceComponent, PropertySurfaceComponentDto } from '../../surface/surface.component';

export interface PropertyKeyInfoModel {
  surface: PropertySurfaceComponentDto;
  subtype: PropertySubtypesDto;
  availability: PropertyAvailabilityDto;
  coOwnership: PropertyCoOwnershipDto;
}

@Component({
  selector: 'etxe-property-key-infos-form',
  standalone: true,
  imports: [
    CommonModule,
    PropertySurfaceComponent,
    PropertyAvailabilityComponent,
    PropertyCoOwnershipComponent,
    PropertySubtypesComponent,
  ],
  templateUrl: './key-infos-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyKeyInfosFormComponent extends AbstractSignalFormComponent<
  PropertyKeyInfoDto,
  PropertyKeyInfoModel
> {
  propertyDefinition = input.required<PropertyDefinitionDto>();
  cadastral = input<PropertyCadastralDto | null | undefined>();
  hideStepHeader = input<boolean>(false);
  sellingPriceCents = input<number | undefined>();

  cadastralSurface = computed(() => {
    const cad = this.cadastral();
    if (cad?.totalSurface != null) return cad.totalSurface;
    if (cad?.parcels && cad.parcels.length > 0) {
      return cad.parcels.reduce((sum, p) => sum + (p.surface || 0), 0);
    }
    return undefined;
  });

  isCoOwnershipApplicable = computed(
    () => this.propertyDefinition()?.propertyType === PropertyType.APARTMENT,
  );

  override model: WritableSignal<PropertyKeyInfoModel> = signal<PropertyKeyInfoModel>({
    surface: {},
    subtype: {},
    availability: {},
    coOwnership: {},
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  override formTree = form(this.model, (_b) => {});

  onSurfaceChanged(surface: PropertySurfaceComponentDto) {
    this.model.update((m) => ({ ...m, surface }));
  }

  onSubtypesChanged(subtype: PropertySubtypesDto) {
    this.model.update((m) => ({ ...m, subtype }));
  }

  onAvailabilityChanged(availability: PropertyAvailabilityDto) {
    this.model.update((m) => ({ ...m, availability }));
  }

  onCoOwnershipChanged(coOwnership: PropertyCoOwnershipDto) {
    this.model.update((m) => ({ ...m, coOwnership }));
  }

  protected override buildModelFromDto(dto: PropertyKeyInfoDto): PropertyKeyInfoModel {
    return {
      surface: {
        habitableSurface: dto?.surface?.habitable,
        groundSurface: dto?.surface?.ground,
        landSurface: dto?.surface?.land ?? this.cadastralSurface(),
        cadastralSurface: this.cadastralSurface(),
        propertyType: this.propertyDefinition()?.propertyType,
      },
      subtype: {
        propertyType: this.propertyDefinition()?.propertyType,
        subtype: dto?.subtype,
        accessibility: dto?.accessibility,
      },
      availability: dto?.availability ?? {},
      coOwnership: dto?.coOwnership ?? {
        isInCoOwnership: this.isCoOwnershipApplicable() ? null : false,
      },
    };
  }

  protected override buildDtoFromModel(model: PropertyKeyInfoModel): PropertyKeyInfoDto {
    const rawDto: PropertyKeyInfoDto = {
      surface: {
        habitable: model.surface.habitableSurface ?? 0,
        ground: model.surface.groundSurface ?? undefined,
        land: model.surface.landSurface ?? undefined,
      },
      subtype: model.subtype.subtype ?? ('' as unknown as PropertyKeyInfoDto['subtype']),
      accessibility: model.subtype.accessibility ?? undefined,
      availability: model.availability as PropertyAvailabilityDto,
      coOwnership: this.isCoOwnershipApplicable()
        ? (model.coOwnership as PropertyCoOwnershipDto)
        : { isInCoOwnership: false },
    };

    return PropertyDataCleaner.cleanPropertyData(
      rawDto as unknown as Record<string, unknown>,
    ) as unknown as PropertyKeyInfoDto;
  }
}
